'use client'

import { use, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AuthButton } from '@/components/AuthButton/auth-button'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { Loader2 } from 'lucide-react'
import { useClientTranslation } from '@/hooks/useClientTranslation'

import serverActionClearCache from '@/helpers/serverActionClearCache'
import { ROUTES } from '@/constants/routes'
import logo from '../../../../../public/images/logo-indigo.png'
import { AuthHeader } from '@/components/AuthHeader/auth-header'
import { getBrand } from '@/components/AuthHeader/brands'
import { showAlert } from '@/stores/redux/features/alerts/slice'
import RetroTerminal from '@/components/RetroTerminal/RetroTerminal'
import { loginWithCredentials } from '@/actions/auth'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import LanguageSwitcherPublic from '@/components/LanguageSwitcher/LanguageSwitcherPublic'

const LoginPage = ({ params }: { params: Promise<{ lng: string }> }) => {
  const { lng } = use(params)
  const { t } = useClientTranslation('auth')
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [retrievePassword, setRetrievePassword] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const signupSuccess = searchParams.get('signup') === 'success'
  const signupPending = searchParams.get('signup') === 'pending'
  const errorParam = searchParams.get('error')
  // Honor a callbackUrl (e.g. the MeOnSteroids desktop-auth bridge) — only
  // same-origin relative paths, so it can't be used as an open redirect.
  const rawCb = searchParams.get('callbackUrl')
  // brand may arrive as a direct ?brand= param, or nested inside the callbackUrl that the
  // auth middleware preserved (e.g. /desktop-auth?...&brand=meonsteroids). Try both.
  const brandFromCb = (() => {
    try {
      const cb = searchParams.get('callbackUrl')
      if (!cb) return null
      // callbackUrl is a path+query; parse its query for brand.
      const qs = cb.includes('?') ? cb.slice(cb.indexOf('?') + 1) : ''
      return new URLSearchParams(qs).get('brand')
    } catch { return null }
  })()
  const brand = getBrand(searchParams.get('brand') || brandFromCb)
  const callbackUrl = rawCb && rawCb.startsWith('/') && !rawCb.startsWith('//') ? rawCb : null
  // Handle both custom 'inactive' error and NextAuth 'AccessDenied' error
  const accountInactive = errorParam === 'inactive' || errorParam === 'AccessDenied' || errorParam === 'INACTIVE_ACCOUNT'
  // Handle user not found error
  const userNotFound = errorParam === 'notfound' || errorParam === 'USER_NOT_FOUND'
  // Handle wrong password error
  const wrongPassword = errorParam === 'wrongpassword' || errorParam === 'WRONG_PASSWORD'
  // Handle OAuth account trying to login with password
  const oauthAccount = errorParam === 'OAUTH_ACCOUNT'
  // Handle generic credentials error (CredentialsSignin, INVALID_FIELDS, SERVER_ERROR)
  const credentialsError = (errorParam === 'CredentialsSignin' || errorParam === 'INVALID_FIELDS' || errorParam === 'SERVER_ERROR')
    && !accountInactive && !userNotFound && !wrongPassword && !oauthAccount

  const signInWithEmail = async (formData: FormData) => {
    try {
      console.log('Sign in with email - form submitted')
      setIsLoading(true)

      // Use server action to validate credentials and get specific error codes
      const result = await loginWithCredentials(formData)

      console.log('Login result:', result)

      if (result.error) {
        setIsLoading(false)
        // Redirect with specific error code in URL params
        router.push(`/${lng}/login?error=${result.error}`)
        return
      }

      // Success - redirect to chat
      console.log('Sign in successful')
      serverActionClearCache(`/${lng}/`)
      router.push(callbackUrl || ROUTES.CHAT(lng))
    } catch (error: any) {
      console.error('Error signing in with email:', error)
      setIsLoading(false)
      dispatch(
        showAlert({
          severity: 'error',
          message: t('login.error'),
        })
      )
    }
  }

  const sendPasswordResetEmail = async (formData: FormData) => {
    try {
      const email = formData.get('email')?.toString()

      if (!email) {
        dispatch(
          showAlert({
            severity: 'error',
            message: t('resetPassword.enterEmail'),
          })
        )
        return
      }

      setIsLoading(true)

      const response = await fetch('/api/auth/send-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        throw new Error('Failed to send password reset email')
      }

      const data = await response.json()
      dispatch(
        showAlert({
          severity: 'success',
          message:
            data.message ||
            t('resetPassword.success'),
        })
      )

      setRetrievePassword(false)
    } catch (error) {
      console.error('Error sending password reset email:', error)
      dispatch(
        showAlert({
          severity: 'error',
          message: t('resetPassword.error'),
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    if (retrievePassword) {
      await sendPasswordResetEmail(formData)
    } else {
      await signInWithEmail(formData)
    }
  }

  return (
    <div className="flex h-screen w-full relative">
      <LanguageSwitcherPublic />
      <div className="flex-1 hidden sm:block">
        <RetroTerminal />
      </div>
      <div className="flex-1 flex flex-col justify-center items-center">
        <AuthHeader brand={brand} />
        <Card className="w-full max-w-[400px] mx-4">
          <CardContent className="pt-6">
            <h1 className="text-xl font-semibold text-center mb-4">
              {!retrievePassword ? t('login.title') : t('resetPassword.title')}
            </h1>

            {signupSuccess && (
              <Alert variant="success" className="mb-4">
                <AlertDescription>
                  {t('login.accountCreated')}
                </AlertDescription>
              </Alert>
            )}

            {signupPending && (
              <Alert className="mb-4 border-amber-500 bg-amber-50 text-amber-800">
                <AlertDescription>
                  {t('login.accountPendingActivation') || 'Your account has been created and is pending activation. You will receive an email once an administrator activates your account.'}
                </AlertDescription>
              </Alert>
            )}

            {accountInactive && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>
                  {t('login.accountInactive') || 'Your account is not yet activated. Please wait for an administrator to activate your account.'}
                </AlertDescription>
              </Alert>
            )}

            {userNotFound && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>
                  {t('login.userNotFound') || 'No account found with this email address.'}{' '}
                  <Link href={ROUTES.SIGNUP(lng)} className="font-semibold underline hover:no-underline">
                    {t('login.registerHere') || 'Register here'}
                  </Link>
                </AlertDescription>
              </Alert>
            )}

            {wrongPassword && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>
                  {t('login.wrongPassword') || 'Incorrect password. Please try again or use the forgot password option.'}
                </AlertDescription>
              </Alert>
            )}

            {oauthAccount && (
              <Alert className="mb-4 border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-700">
                <AlertDescription>
                  {t('login.oauthAccount') || 'This account was created with Google. Please use the "Sign in with Google" button below, or reset your password to create one.'}
                  <button
                    onClick={() => setRetrievePassword(true)}
                    className="block mt-2 font-semibold underline hover:no-underline"
                  >
                    {t('login.resetPasswordLink') || 'Reset password'}
                  </button>
                </AlertDescription>
              </Alert>
            )}

            {credentialsError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>
                  {t('login.error') || 'Login failed. Please try again.'}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  aria-label="login-email-input"
                />
              </div>

              {!retrievePassword && (
                <div className="space-y-2">
                  <Label htmlFor="password">{t('login.password')}</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    aria-label="login-password-input"
                  />
                </div>
              )}

              {!retrievePassword && (
                <div className="flex items-center space-x-2">
                  <Checkbox id="remember" name="remember" aria-label="login-remember-checkbox" />
                  <Label htmlFor="remember" className="text-sm font-normal">
                    {t('login.remember')}
                  </Label>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
                aria-label={retrievePassword ? "login-reset-password-button" : "login-submit-button"}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLoading
                  ? !retrievePassword
                    ? t('login.signingIn')
                    : t('resetPassword.sending')
                  : !retrievePassword
                  ? t('login.button')
                  : t('resetPassword.button')}
              </Button>
            </form>

            <div className="flex justify-between mt-4 text-sm">
              <button
                onClick={() => setRetrievePassword(!retrievePassword)}
                className="text-primary hover:underline"
                aria-label={retrievePassword ? "login-back-to-login-link" : "login-forgot-password-link"}
              >
                {retrievePassword
                  ? t('resetPassword.backToLogin')
                  : t('login.forgotPassword')}
              </button>
              {!retrievePassword && (
                <Link href={ROUTES.SIGNUP(lng)} className="text-primary hover:underline" aria-label="login-signup-link">
                  {t('login.noAccount')} {t('login.signUpLink')}
                </Link>
              )}
            </div>

            {!retrievePassword && (
              <div className="mt-4">
                <AuthButton />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default LoginPage
